import React, { useEffect, useMemo, useRef } from 'react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import Image from '@/shared/components/shared/Image';
import { tp } from '@/shared/lib/typograf';
import { formatDateString } from '@/shared/lib/utils/formatDateString';
import Button from '../../shared/Button';
import { ProgramCardSmallProps } from '../../shared/ProgramCard/ProgramCard';
import Typografed from '../../shared/Typografed';

export type DirectionsSectionProps = {
    title: string;
    descr?: string;
    link?: {
        href: string;
        name: string;
    } | null;
    directions: ProgramCardSmallProps[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    directionsSectionProps: DirectionsSectionProps;
};

export const SECTION_ID = 'directions';

export const isVisibleDirection = (direction: ProgramCardSmallProps) =>
    Boolean(direction.title) && Boolean(direction.image?.src);

const DirectionsSection = ({ directionsSectionProps }: Props) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const cardsWrapperRef = useRef<HTMLDivElement>(null);
    const device = useMediaQueryDeviceStateValue();
    const isTablet = device.includes('tablet');
    const isMobile = device.includes('mobile');
    const directions = useMemo(
        () => directionsSectionProps.directions.filter(isVisibleDirection),
        [directionsSectionProps.directions],
    );

    useEffect(() => {
        const calculateCardHeight = () => {
            if (cardsWrapperRef.current) {
                const cards = Array.from(cardsWrapperRef.current.querySelectorAll<HTMLElement>('.js-direction__card'));
                cards.forEach((card) => card.style.removeProperty('height'));
                const maxHeight = Math.max(...cards.map((card) => card.offsetHeight));
                if (maxHeight > 0) {
                    cardsWrapperRef.current.style.setProperty('--max-card-height', `${maxHeight}px`);
                }
            }
        };

        const timeoutId = setTimeout(calculateCardHeight, 0);
        window.addEventListener('resize', calculateCardHeight);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', calculateCardHeight);
        };
    }, [directions]);

    if (directions.length === 0) {
        return null;
    }

    return (
        <section
            id={SECTION_ID}
            className="directions-section js-header-theme-trigger"
            data-theme="dark"
            style={
                {
                    '--total-count': directions.length,
                } as React.CSSProperties
            }
            ref={cardsWrapperRef}
        >
            <div className="directions-wrapper wrapper">
                <div className="directions-section__header">
                    <div className="directions-section__title h2">
                        <Typografed>{directionsSectionProps.title}</Typografed>
                    </div>
                    <div className="directions-section__info">
                        {directionsSectionProps.descr && (
                            <div
                                className="directions-section__info-text text-s"
                                dangerouslySetInnerHTML={{
                                    __html: tp(directionsSectionProps.descr),
                                }}
                            ></div>
                        )}
                        {directionsSectionProps.link && (
                            <Button tag="a" href={directionsSectionProps.link.href} variant={`outline-${'dark'}`}>
                                <Typografed>{directionsSectionProps.link.name || 'Архив программы 2025'}</Typografed>
                            </Button>
                        )}
                    </div>
                </div>
                <div className="directions-section__cards-wrapper">
                    {directions.map((direction, index) => {
                        const dateStart = direction.publishedAt ? formatDateString(direction.publishedAt) : undefined;
                        const dateEnd = direction.endedAt ? formatDateString(direction.endedAt) : undefined;
                        return (
                            <div
                                className="direction__card js-direction__card"
                                key={`${direction.title}-${index}`}
                                ref={index === 0 ? cardRef : null}
                                style={{
                                    transform: `translateY(calc((var(--offset)) * ${isMobile ? index / 2 : index}))`,
                                }}
                            >
                                <div className="direction__card-wrapper">
                                    <div className="direction__card-content-container">
                                        <div className="direction__card-content-top">
                                            {direction.title && (
                                                <div className="direction__card-title text-xl">{direction.title}</div>
                                            )}
                                            {direction.description && (
                                                <p
                                                    className="direction__card-description text-opacity wysiwyg"
                                                    dangerouslySetInnerHTML={{
                                                        __html: tp(direction.description),
                                                    }}
                                                ></p>
                                            )}
                                        </div>
                                        <div className="direction__card-content-date-wrapper">
                                            {dateStart && dateStart.day && (
                                                <span className="direction__card-content-date direction__card-content-date--big">
                                                    {dateStart.day}
                                                </span>
                                            )}
                                            {dateEnd && dateEnd.day && (
                                                <span className="direction__card-content-date direction__card-content-date--big">
                                                    {` - ${dateEnd.day}`}
                                                </span>
                                            )}
                                            {dateStart && dateStart.month && (
                                                <span className="direction__card-content-month">{dateStart.month}</span>
                                            )}
                                            {dateStart && dateStart.year && (
                                                <span className="direction__card-content-year">{dateStart.year}</span>
                                            )}
                                        </div>
                                    </div>
                                    {direction.image?.src && (
                                        <div className="direction__card-image-wrapper">
                                            <Image
                                                src={direction.image.src}
                                                alt={direction.image.alt || ''}
                                                height={direction.image.height || 1}
                                                width={direction.image.width || 1}
                                                className="direction__card-image"
                                                sizes="(max-width: 767px) 90vw, (max-width: 1360px) 50vw, 60vw"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
export default DirectionsSection;
